import { CustomScheduleAtV1 } from '@/components/ats/job-stage-actions/actions/email/CustomScheduleAtV1';
import { useApplicationHelper } from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { useAuth } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { Flex } from '@chakra-ui/react';
import { useField } from 'formik';
import { useEffect, useState, useMemo } from 'react';
import { RejectEmailComposer } from './RejectEmailComposer';

export const RejectEmailPreview = ({
  formik,
  contact,
  templateOptions,
  initialValues = null,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState({
    id: undefined,
    body: initialValues?.body || undefined,
    subject: initialValues?.subject || undefined,
    from: initialValues?.from || undefined,
    cc: initialValues?.cc || undefined,
    bcc: initialValues?.bcc || undefined,
    attachments: initialValues?.attachments || undefined,
    fromUserObject: initialValues?.fromUserObject || undefined,
  });
  const [, { value }] = useField('selectedTemplate');

  const { values, setValues, setFieldValue } = formik || {};
  const { currUser } = useAuth();

  const { feSharedEmails } = useKFlags();

  const { applicationData } = useApplicationHelper();

  useEffect(() => {
    const { body, subject, id } = selectedTemplate || {};

    const fromUserObject = selectedTemplate?.fromUserObject ||
      initialValues?.fromUserObject || {
        id: currUser?.id,
        email: currUser?.email,
        name: `${currUser?.first_name ?? ''} ${
          currUser?.last_name ?? ''
        }`.trim(),
        avatar: currUser?.avatar,
      };

    if (id || body || subject) {
      setValues({
        ...values,
        body,
        subject,
        // The composer's From select owns the sender; seeding it here hides an unset one.
        cc: selectedTemplate?.cc,
        bcc: selectedTemplate?.bcc,
        attachments: selectedTemplate?.attachments,
        ...(feSharedEmails ? { fromUserObject } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, currUser, contact]);

  useEffect(() => {
    setSelectedTemplate((prev) => (prev !== value ? value : prev));
  }, [value]);

  const {
    body = initialValues?.body || '',
    subject = initialValues?.subject || '',
    // Only the stored sender; the composer resolves its own default.
    from = initialValues?.from,
    cc = initialValues?.cc || [],
    bcc = initialValues?.bcc || [],
    attachments = initialValues?.attachments || [],
  } = selectedTemplate || {};

  const selectedSurvey = useMemo(() => {
    const survey = applicationData?.ats_rejection_email?.survey || null;
    if (!survey) return null;
    return {
      value: survey?.ats_form_template?.id,
      label: survey?.ats_form_template?.title,
    };
  }, [applicationData]);

  if (!values?.sendRejectEmail) {
    return null;
  }

  return (
    <>
      <Flex flexDir="column" overflowY="auto" borderRadius="sm">
        <RejectEmailComposer
          // Seed only; `initialValues` spreads first so its null `from` can't win.
          config={{
            ...initialValues,
            body,
            subject,
            from,
            cc,
            bcc,
          }}
          candidateEmail={contact?.email}
          templates={templateOptions}
          // Bumps on every Reject click, which surfaces Email's inline errors.
          submitAttempt={formik?.submitCount ?? 0}
          attachments={values?.attachments ?? attachments}
          onAttachmentsChange={(next) => setFieldValue('attachments', next)}
          onTemplateChange={(template) =>
            setFieldValue('selectedTemplate', template)
          }
          selectedSurvey={selectedSurvey}
          onChange={(val) => {
            setFieldValue('rejectEmailConfig', {
              ...(formik?.values?.rejectEmailConfig || {}),
              ...val,
            });
            // Validation falls back to the top-level `from`; keep it mirrored.
            setFieldValue('from', val.from ?? '');
          }}
          onSurveySelect={(val) => {
            setFieldValue('rejectEmailConfig', {
              ...(formik?.values?.rejectEmailConfig || {}),
              ...{ survey: val },
            });
          }}
          onSurveyRemove={() => {
            setFieldValue('rejectEmailConfig', {
              ...(formik?.values?.rejectEmailConfig || {}),
              ...{ survey: null },
            });
          }}
        />
      </Flex>
      <Flex>
        <CustomScheduleAtV1 />
      </Flex>
    </>
  );
};
